import React, { ReactNode, Ref, useCallback } from 'react';
import styled from 'styled-components';
import { useJigsaw } from '../../context/JigsawContext';
import { palette } from '../../theme/colors';
import { solvSystem, SolvSystemProps, sx, WithSx } from '../../theme/system';
import { SolvTheme } from '../../theme/theme';
import Box, { BoxProps } from '../Box/Box';
import Stack from '../Stack/Stack';

type ButtonOrLinkTypes = HTMLAnchorElement | HTMLButtonElement;

type ButtonProps = React.AnchorHTMLAttributes<HTMLAnchorElement>;
type LinkProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

export type BaseClickableProps = {
  /** Icon to display at the start of the content. */
  startIcon?: ReactNode;

  /** Icon to display at the end of the content. */
  endIcon?: ReactNode;

  /** Routes using the `href` tag and browser behavior, instead of history */
  external?: boolean;

  /** Whether the element is disabled. */
  disabled?: boolean;

  /** Whether the element is loading. */
  loading?: boolean;

  /** When a link, open the target in a new window. */
  openInNewWindow?: boolean;

  /** Should send the browser history `back` */
  goBack?: boolean;

  /** A react node to include outside of the button contents container */
  accessory?: ReactNode;

  /** Whether to render the accessory */
  showAccessory?: boolean;

  /** Whether to render the element as inline-flex */
  inline?: boolean;

  /** Box props to apply to the wrapper around `children` to create custom layouts */
  contentProps?: BoxProps;

  /** Disable the default 'focusable' prop */
  disableDefaultFocusStyles?: boolean;

  /**
   * Segment tracking event name on click
   */
  event?: string;

  /**
   * Payload to be included in a tracking event
   */
  meta?: Record<string, any>;
} & (LinkProps & ButtonProps & SolvSystemProps & WithSx);

const ResetButton = styled.button<BaseClickableProps>`
  cursor: pointer;
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  text-align: left;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  display: ${(props) => (props.inline ? 'inline-flex' : 'flex')};
  align-items: center;
  position: relative;
  border-radius: ${SolvTheme.radii.base};
  color: ${palette.gray[600]};

  &.disabled {
    cursor: auto;
    pointer-events: none;
  }
  ${solvSystem}
  ${sx}
`;

const ResetLink = styled.a<BaseClickableProps>`
  text-decoration: none;
  padding: 0;
  margin: 0;
  text-align: left;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  display: ${(props) => (props.inline ? 'inline-flex' : 'flex')};
  align-items: center;
  position: relative;
  border-radius: ${SolvTheme.radii.base};
  color: ${palette.gray[600]};

  &:hover,
  &:active,
  &:visited {
    color: unset;
  }

  &.disabled {
    cursor: auto;
    pointer-events: none;
  }
  ${solvSystem}
  ${sx}
`;

const BaseClickable = React.forwardRef(
  (
    {
      children,
      disabled,
      loading,
      onClick,
      startIcon,
      endIcon,
      accessory,
      external,
      openInNewWindow,
      href,
      goBack,
      disableDefaultFocusStyles,
      event: trackingEvent,
      meta,
      showAccessory = false,
      contentProps = {},
      type,
      ...restProps
    }: BaseClickableProps,
    ref: Ref<any>,
  ) => {
    const { history, analytics } = useJigsaw();
    // Intercept click to handle disabled and loading state
    const handleClick = useCallback(
      (event: React.MouseEvent<any>) => {
        if (disabled || loading) {
          event.preventDefault();
        }

        if (!external) {
          if (goBack) {
            event.preventDefault();
            history.goBack();
          } else if (href !== undefined) {
            event.preventDefault();
            history.push(href);
          }
        }

        // Always call the onClick handler if it's defined -- we may want actions
        // on top of the Link handling as well
        if (onClick) {
          onClick(event);
        }

        if (trackingEvent) {
          analytics?.trackEvent?.(trackingEvent, meta);
        }
      },
      [
        analytics,
        disabled,
        external,
        goBack,
        history,
        href,
        loading,
        meta,
        onClick,
        trackingEvent,
      ],
    );

    const props: LinkProps & ButtonProps = {};

    const classname =
      (disableDefaultFocusStyles ? '' : 'focusable ') +
      (disabled ? ' disabled ' : '') +
      (restProps.className ?? '');

    if (href) {
      if (openInNewWindow) {
        props.target = '__BLANK';

        // Avoid tab hijacking or potential exposure of `window.opener`
        if (props.rel === undefined) {
          props.rel = 'noopener';
        }
      }
      props.onClick = (e: React.MouseEvent<ButtonOrLinkTypes>) => {
        handleClick(e);
      };

      return (
        <ResetLink
          ref={ref}
          href={href}
          {...props}
          {...restProps}
          aria-busy={loading}
          className={classname}
        >
          <Stack
            space={1}
            row
            alignItems="center"
            style={{
              display: 'contents',
              visibility: loading ? 'hidden' : 'visible',
            }}
          >
            {startIcon && startIcon}
            <Box
              as="span"
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ pointerEvents: 'none' }}
              {...contentProps}
            >
              {children}
            </Box>
            {endIcon && endIcon}
          </Stack>
          {showAccessory && accessory}
        </ResetLink>
      );
    }

    return (
      <ResetButton
        ref={ref}
        onClick={handleClick}
        aria-busy={loading}
        disabled={disabled}
        type={type}
        {...props}
        {...restProps}
        className={classname}
      >
        <Stack
          space={1}
          row
          alignItems="center"
          style={{
            display: 'contents',
            visibility: loading ? 'hidden' : 'visible',
          }}
        >
          {startIcon && startIcon}
          <Box
            as="span"
            justifyContent="center"
            display="flex"
            alignItems="center"
            sx={{ pointerEvents: 'none' }}
            {...contentProps}
            width="100%"
          >
            {children}
          </Box>
          {endIcon && endIcon}
        </Stack>
        {showAccessory && accessory}
      </ResetButton>
    );
  },
);

BaseClickable.displayName = 'BaseClickable';
export default BaseClickable;
